<script lang="ts" generics="T">
  import {
    Table,
    EmptyState,
    Error as ErrorComponent,
    Pagination,
    Skeleton,
  } from "@poolsideai/web-components";
  import type { Snippet } from "svelte";

  interface Props<T> {
    loading: boolean;
    error?: string;
    data: T[];
    currentPage: number;
    onPageChange: (page: number, shouldFetchMore?: boolean) => void;
    emptyStateTitle?: string;
    emptyStateDescription?: string;
    fullHeight?: boolean;
    tableHeight?: string;
    skeletonColumns?: number;
    tableHead?: Snippet;
    tableBody?: Snippet<[T[]]>;
    totalPages?: number;
    pageSize?: number;
    onPageSizeChange?: (pageSize: number) => void;
  }

  const {
    loading,
    error,
    data,
    currentPage,
    onPageChange,
    emptyStateTitle = "No Data",
    emptyStateDescription = "No items found",
    tableHeight = "h-[450px]",
    fullHeight = false,
    skeletonColumns = 4,
    tableHead,
    tableBody,
    totalPages: serverTotalPages,
    pageSize: serverPageSize,
    onPageSizeChange,
  }: Props<T> = $props();

  const PAGE_SIZE_OPTIONS = [10, 25, 50];
  let computedRowCount = $state(10);
  let tableContainer = $state<HTMLDivElement>();

  $effect(() => {
    if (!tableContainer) return;
    const update = () => {
      if (!tableContainer) return;
      const availableHeight = tableContainer.clientHeight - 45; // minus header
      computedRowCount = Math.max(5, Math.min(50, Math.floor(availableHeight / 40)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(tableContainer);
    return () => ro.disconnect();
  });

  const isServerSide = $derived(serverTotalPages !== undefined);
  const pageSize = $derived(serverPageSize ?? computedRowCount);
  const totalPages = $derived(isServerSide ? serverTotalPages! : Math.ceil(data.length / pageSize));

  const displayData = $derived.by(() => {
    if (isServerSide) return data;
    const start = (currentPage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  });

  function handlePageChange(page: number) {
    if (page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page, page >= totalPages - 1);
  }
</script>

<div class="flex h-full w-full flex-col gap-2">
  {#if error}
    <div class="mb-4">
      <ErrorComponent {error} type="area" />
    </div>
  {/if}

  <div class={`max-h-full ${fullHeight ? "h-full" : tableHeight}`} bind:this={tableContainer}>
    {#if loading}
      <Table>
        {#if tableHead}{@render tableHead()}{/if}
        <Table.Body>
          {#each { length: pageSize } as _}
            <Table.Row>
              {#each { length: skeletonColumns } as _}
                <Table.Cell><Skeleton height={20} /></Table.Cell>
              {/each}
            </Table.Row>
          {/each}
        </Table.Body>
      </Table>
    {:else if data.length === 0}
      <EmptyState {fullHeight} description={emptyStateDescription} title={emptyStateTitle} />
    {:else}
      <Table>
        {#if tableHead}{@render tableHead()}{/if}
        {#if tableBody}{@render tableBody(displayData)}{/if}
      </Table>
    {/if}
  </div>

  <div
    class="mt-2 flex items-center"
    class:mt-4={isServerSide}
    class:justify-between={isServerSide && onPageSizeChange}
    class:justify-center={!isServerSide || !onPageSizeChange}
  >
    {#if isServerSide && onPageSizeChange}
      <div class="flex items-center gap-2">
        <span class="text-sm text-foreground-secondary">Rows per page:</span>
        <div class="flex gap-1">
          {#each PAGE_SIZE_OPTIONS as size}
            <button
              type="button"
              class="rounded px-3 py-1 text-sm transition-colors {serverPageSize === size
                ? 'bg-mono-300 text-foreground-primary'
                : 'hover:bg-mono-250 bg-mono-200 text-foreground-secondary'}"
              onclick={() => onPageSizeChange(size)}
            >
              {size}
            </button>
          {/each}
        </div>
      </div>
    {/if}
    <Pagination {currentPage} onPageChange={handlePageChange} {totalPages} />
  </div>
</div>
